/**
 * ─────────────────────────────────────────────────────────────────────
 * FILE: src/contexts/NotificationContext.tsx
 * DESCRIPTION: Global notification center state management.
 *              Captures, stores, and manages user notifications across
 *              the app. Any page or service can emit a notification;
 *              users review and dismiss them via the Header's
 *              Notification Center.
 *
 * FEATURES:
 *   - Typed notifications (info / success / warning / error)
 *   - Categorized for filtering (workspace / organization / profile / ...)
 *   - Unread-count tracking with badge support
 *   - markAsRead / markAllAsRead / dismiss / clearAll actions
 *   - localStorage persistence (Beta: no backend required)
 *   - Auto-cap to prevent unbounded growth (MAX_NOTIFICATIONS)
 *   - Optional action buttons (label + route path)
 *
 * PERSISTENCE NOTE:
 *   During Beta, notifications persist to localStorage per-browser.
 *   Phase 5 (Supabase) migrates storage to a `notifications` table so
 *   notifications follow the user across devices.
 *
 * USAGE:
 *   // Emit from any component/page:
 *   const { notify } = useNotifications();
 *   notify({
 *     type: 'success',
 *     title: 'Workspace ready',
 *     message: 'Your personal workspace was created.',
 *     category: 'workspace',
 *   });
 *
 *   // Consume in the Header Notification Center:
 *   const { notifications, unreadCount, dismiss } = useNotifications();
 *
 * @copyright 2025-2026 Anou. All rights reserved.
 * @author Anou
 * @version 1.0.0
 * @created 2026-09-05
 * ─────────────────────────────────────────────────────────────────────
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react';

// ─────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────
export type NotificationType = 'info' | 'success' | 'warning' | 'error';

export type NotificationCategory =
  | 'workspace'
  | 'organization'
  | 'profile'
  | 'onboarding'
  | 'analysis'
  | 'system';

export interface AppNotification {
  id: string;
  type: NotificationType;
  title: string;
  message: string;
  category: NotificationCategory;
  createdAt: number;        // epoch milliseconds
  read: boolean;
  actionLabel?: string;     // optional CTA text
  actionPath?: string;      // optional route to navigate to on click
}

/** Payload for creating a notification (id / createdAt / read auto-set). */
export type NotificationInput = Omit<AppNotification, 'id' | 'createdAt' | 'read'>;

interface NotificationContextValue {
  notifications: AppNotification[];
  unreadCount: number;
  /** Emit a new notification. Returns its generated id. */
  notify: (input: NotificationInput) => string;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  dismiss: (id: string) => void;
  clearAll: () => void;
}

// ─────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────
const STORAGE_KEY = 'verimark_notifications';
const MAX_NOTIFICATIONS = 50;

const NotificationContext = createContext<NotificationContextValue | undefined>(undefined);

// ─────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────
const generateId = (): string => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `notif_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
};

const loadFromStorage = (): AppNotification[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

// ─────────────────────────────────────────────────────────────────────
// Provider
// ─────────────────────────────────────────────────────────────────────
export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [notifications, setNotifications] = useState<AppNotification[]>(() => loadFromStorage());

  // Persist to localStorage whenever notifications change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(notifications));
    } catch {
      // Storage full or unavailable — fail silently, never crash the app
    }
  }, [notifications]);

  /** Emit a new notification (prepended, newest first). Returns its id. */
  const notify = useCallback((input: NotificationInput): string => {
    const id = generateId();
    const notification: AppNotification = {
      ...input,
      id,
      createdAt: Date.now(),
      read: false,
    };
    setNotifications(prev => [notification, ...prev].slice(0, MAX_NOTIFICATIONS));
    return id;
  }, []);

  const markAsRead = useCallback((id: string) => {
    setNotifications(prev => prev.map(n => (n.id === id ? { ...n, read: true } : n)));
  }, []);

  const markAllAsRead = useCallback(() => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  }, []);

  const dismiss = useCallback((id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  const clearAll = useCallback(() => {
    setNotifications([]);
  }, []);

  const unreadCount = useMemo(
    () => notifications.filter(n => !n.read).length,
    [notifications]
  );

  const value = useMemo(
    () => ({ notifications, unreadCount, notify, markAsRead, markAllAsRead, dismiss, clearAll }),
    [notifications, unreadCount, notify, markAsRead, markAllAsRead, dismiss, clearAll]
  );

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

// ─────────────────────────────────────────────────────────────────────
// Hook
// ─────────────────────────────────────────────────────────────────────
export const useNotifications = (): NotificationContextValue => {
  const ctx = useContext(NotificationContext);
  if (!ctx) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return ctx;
};
